// Google Analytics & GTM tracking utilities
export const GA_ID = 'G-0ZJQ8JWM3Z';
export const GTM_ID = 'GTM-TLLR4GP4';

// Extend Window interface for TypeScript
declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Push data to GTM dataLayer
 */
function pushToDataLayer(data: Record<string, any>) {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  }
}

/**
 * Track page view (for SPA navigation)
 * @param path - Page path (e.g., /hotels/millennium)
 * @param title - Page title
 */
export function pageview(path: string, title?: string) {
  const pageTitle = title || document.title;
  
  // GTM: Virtual pageview
  pushToDataLayer({
    event: 'virtual_pageview',
    page_path: path,
    page_title: pageTitle,
    page_location: window.location.href,
  });

  // GA4 via gtag
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: pageTitle,
      page_location: window.location.href,
      send_to: GA_ID,
    });
  }
}

/**
 * Track custom events
 * @param action - Event name/action
 * @param params - Event parameters
 */
export function trackEvent(action: string, params: Record<string, any> = {}) {
  // GTM
  pushToDataLayer({
    event: action,
    ...params,
  });

  // GA4 via gtag
  if (window.gtag) {
    window.gtag('event', action, params);
  }
}

/**
 * Track outbound link clicks (for ads/external links)
 * @param url - Destination URL
 * @param label - Optional label
 */
export function trackOutboundLink(url: string, label?: string) {
  trackEvent('outbound_click', {
    link_url: url,
    link_domain: new URL(url).hostname,
    link_label: label || url,
  });
}

/**
 * Track WhatsApp message sends
 * @param context - Where the message was sent from
 * @param params - Additional parameters
 */
export function trackWhatsAppSend(context: string, params: Record<string, any> = {}) {
  trackEvent('whatsapp_send', {
    context,
    ...params,
  });
}

/**
 * Track booking actions
 * @param action - Booking action (e.g., 'book_now_click', 'booking_form_submit')
 * @param params - Booking details
 */
export function trackBooking(action: string, params: Record<string, any> = {}) {
  trackEvent(action, {
    event_category: 'booking',
    ...params,
  });
}

/**
 * Track hotel/package interactions
 * @param action - Action type
 * @param itemType - 'hotel' or 'package'
 * @param itemId - Item ID
 * @param itemName - Item name
 * @param params - Additional parameters
 */
export function trackItemInteraction(
  action: string,
  itemType: 'hotel' | 'package',
  itemId: string,
  itemName: string,
  params: Record<string, any> = {}
) {
  trackEvent(action, {
    item_type: itemType,
    item_id: itemId,
    item_name: itemName,
    ...params,
  });
}

/**
 * Track form interactions
 * @param formName - Name of the form
 * @param action - Action (e.g., 'form_start', 'form_submit', 'form_error')
 * @param params - Additional parameters
 */
export function trackForm(formName: string, action: string, params: Record<string, any> = {}) {
  trackEvent(`${action}`, {
    form_name: formName,
    event_category: 'form',
    ...params,
  });
}

/**
 * Track scroll depth
 * @param percentage - Scroll percentage (25, 50, 75, 100)
 */
export function trackScrollDepth(percentage: number) {
  trackEvent('scroll_depth', {
    scroll_percentage: percentage,
  });
}

/**
 * Track video interactions
 * @param action - Action (e.g., 'video_start', 'video_complete')
 * @param videoTitle - Video title
 */
export function trackVideo(action: string, videoTitle: string) {
  trackEvent(action, {
    video_title: videoTitle,
    event_category: 'video',
  });
}



